/**
 * perfProbeCore — dependency-free bookkeeping for the chat frontend perf probe.
 *
 * This module has NO imports (no React, no DOM) so it can be unit-tested in the
 * `node` jest environment and reasoned about in isolation. The React bindings
 * (hooks, <ProfiledRegion>, window API, enablement) live in ./perfProbe.tsx.
 *
 * It answers two questions for a long-conversation session:
 *   1. "What re-renders without need?" — recordRender() counts renders per
 *      component and, given the values a component actually reads, attributes
 *      each update to the value that changed. An update where NONE of those
 *      values changed is a wasted render (identical output, pure overhead).
 *   2. "How expensive is each render?" — recordCommit() aggregates React commit
 *      durations so we can see total/avg/max/p95 work per component.
 */

export type RenderPhase = 'mount' | 'update';

export interface CommitStats {
  count: number;
  total: number;
  avg: number;
  max: number;
  p95: number;
}

export interface ComponentStats {
  name: string;
  renders: number;
  mounts: number;
  updates: number;
  /** updates where every watched value was reference-identical → output could not have changed */
  wastedUpdates: number;
  /** key → number of updates in which that watched value changed (the re-render triggers) */
  triggers: Record<string, number>;
  commit: CommitStats;
}

export interface ProbeMark {
  label: string;
  ts: number;
}

export interface ProbeSummary {
  durationMs: number;
  marks: ProbeMark[];
  components: ComponentStats[];
  totals: { renders: number; wastedUpdates: number; commitTotalMs: number };
}

const now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

/** Keys whose value differs between two snapshots by Object.is (added/removed keys count as changed). */
export function diffChangedKeys(
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
): string[] {
  const keys = new Set<string>([...Object.keys(prev), ...Object.keys(next)]);
  const changed: string[] = [];
  keys.forEach((k) => {
    if (!Object.is(prev[k], next[k])) changed.push(k);
  });
  return changed;
}

/** Nearest-rank percentile. Returns 0 for an empty input. Order-independent. */
export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = Math.ceil((p / 100) * sorted.length);
  const idx = Math.min(sorted.length - 1, Math.max(0, rank - 1));
  return sorted[idx];
}

interface Entry {
  hookRenders: number;
  mounts: number;
  updates: number;
  wastedUpdates: number;
  triggers: Record<string, number>;
  commitSamples: number[];
  prevWatched?: Record<string, unknown>;
  seen: boolean;
}

// Cap stored commit samples so a very long measurement session can't grow memory
// without bound. 20k samples is far more than any scenario needs.
const MAX_COMMIT_SAMPLES = 20000;

export class RenderRecorder {
  private entries = new Map<string, Entry>();
  private marks: ProbeMark[] = [];
  private startTs = now();

  private entry(name: string): Entry {
    let e = this.entries.get(name);
    if (!e) {
      e = {
        hookRenders: 0,
        mounts: 0,
        updates: 0,
        wastedUpdates: 0,
        triggers: {},
        commitSamples: [],
        seen: false,
      };
      this.entries.set(name, e);
    }
    return e;
  }

  /**
   * Record one render of `name`. `watched` is the set of values the component
   * reads to produce its output; when supplied on consecutive renders we can
   * tell whether the update was wasted (nothing it reads changed) and which
   * value triggered it.
   */
  recordRender(name: string, watched?: Record<string, unknown>): void {
    const e = this.entry(name);
    e.hookRenders += 1;
    if (!e.seen) {
      e.mounts += 1;
      e.seen = true;
    } else {
      e.updates += 1;
      if (watched !== undefined && e.prevWatched !== undefined) {
        const changed = diffChangedKeys(e.prevWatched, watched);
        if (changed.length === 0) {
          e.wastedUpdates += 1;
        } else {
          changed.forEach((k) => {
            e.triggers[k] = (e.triggers[k] || 0) + 1;
          });
        }
      }
    }
    if (watched !== undefined) e.prevWatched = watched;
  }

  /** Record a React commit (render) duration in ms for `name`, from React.Profiler. */
  recordCommit(name: string, ms: number): void {
    const e = this.entry(name);
    if (e.commitSamples.length < MAX_COMMIT_SAMPLES) e.commitSamples.push(ms);
  }

  /** Tag the timeline with a label (e.g. "typing-start") for phase correlation. */
  mark(label: string): void {
    this.marks.push({ label, ts: now() });
  }

  reset(): void {
    this.entries.clear();
    this.marks = [];
    this.startTs = now();
  }

  summarize(): ProbeSummary {
    const components: ComponentStats[] = [];
    this.entries.forEach((e, name) => {
      let total = 0;
      let max = 0;
      for (const ms of e.commitSamples) {
        total += ms;
        if (ms > max) max = ms;
      }
      const count = e.commitSamples.length;
      components.push({
        name,
        renders: e.hookRenders > 0 ? e.hookRenders : count,
        mounts: e.mounts,
        updates: e.updates,
        wastedUpdates: e.wastedUpdates,
        triggers: { ...e.triggers },
        commit: {
          count,
          total,
          avg: count ? total / count : 0,
          max,
          p95: percentile(e.commitSamples, 95),
        },
      });
    });
    // Most expensive first: by total commit time, then by render count.
    components.sort((a, b) => b.commit.total - a.commit.total || b.renders - a.renders);
    return {
      durationMs: now() - this.startTs,
      marks: [...this.marks],
      components,
      totals: {
        renders: components.reduce((a, c) => a + c.renders, 0),
        wastedUpdates: components.reduce((a, c) => a + c.wastedUpdates, 0),
        commitTotalMs: components.reduce((a, c) => a + c.commit.total, 0),
      },
    };
  }
}
